import { RoleSwitchItems } from "@/components/RoleSwitchItems";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, LogOut, UserRound, Upload, Trash2, Loader2, MessageSquareText, Share2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FeedbackModal } from "@/components/FeedbackModal";
import { ShareDialog } from "@/components/ShareDialog";

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "U";

const roleLabel = (r = "") =>
  ({ super_admin: "Super User", admin: "Super User", sales: "Sales", portfolio_owner: "Portfolio Manager",
     tenant_admin: "School Admin", client: "User" }[(r || "").toLowerCase()] || r || "User");

/**
 * Standardized bottom-left user area used across all session-based portals.
 * Click-to-open menu (never hover/auto-open) with My Profile (photo) + Sign Out.
 * Self-fetches /api/auth/me so it always matches the authenticated user.
 */
export const PortalUserMenu = ({ accent = "#1A1F3C", onSignOut, collapsed = false, placement = "sidebar" }) => {
  const [me, setMe] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/auth/me`, cfg).then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const photo = me?.profile_photo_url ? `${API}${me.profile_photo_url}` : null;
  const name = me?.name || me?.email || "User";

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) {
      toast.error("Use a PNG, JPG, or WebP image."); return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await axios.post(`${API}/api/assets/profile-photo`, fd, cfg);
      toast.success("Profile photo updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/api/assets/profile-photo`, cfg); toast.success("Photo removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
    finally { setBusy(false); }
  };

  const Avatar = ({ size = 32 }) => (
    photo ? (
      <img src={photo} alt={`${name} profile photo`} data-testid="user-avatar-img"
        className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />
    ) : (
      <div data-testid="user-avatar-initials"
        className="rounded-full grid place-items-center text-white font-semibold shrink-0"
        style={{ width: size, height: size, backgroundColor: accent, fontSize: size * 0.4 }}>
        {initials(name)}
      </div>
    )
  );

  const topbar = placement === "topbar";
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {topbar ? (
            <button data-testid="portal-user-menu"
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-black/5 transition-colors text-left">
              <Avatar size={32} />
              <div className="min-w-0 hidden sm:block">
                <div className="text-[12px] font-semibold text-[#111827] truncate max-w-[140px]">{name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#6b7280]">{roleLabel(me?.role)}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          ) : (
            <button data-testid="portal-user-menu"
              className="w-full flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-black/5 transition-colors text-left">
              <Avatar size={34} />
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800 truncate">{name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{roleLabel(me?.role)}</div>
                </div>
              )}
              {!collapsed && <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />}
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent side={topbar ? "bottom" : "top"} align={topbar ? "end" : "start"} className="w-[290px] max-w-[92vw] max-h-[80vh] overflow-y-auto">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Avatar size={28} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{name}</div>
              <div className="text-[11px] text-slate-500">{roleLabel(me?.role)}</div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <RoleSwitchItems />
          <DropdownMenuItem data-testid="menu-my-profile" onClick={() => setDlg(true)}>
            <UserRound className="h-4 w-4 mr-2" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="menu-give-feedback" onClick={() => setFbOpen(true)}>
            <MessageSquareText className="h-4 w-4 mr-2" /> Give Us Feedback
          </DropdownMenuItem>
          {["portfolio_owner", "client", "tenant_admin"].includes((me?.role || "").toLowerCase()) && (
            <DropdownMenuItem data-testid="menu-refer-org" onClick={() => setShareOpen(true)}>
              <Share2 className="h-4 w-4 mr-2" /> Refer an Organization
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="menu-sign-out" onClick={onSignOut} className="text-red-600 focus:text-red-600">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-sm" data-testid="my-profile-dialog">
          <DialogHeader>
            <DialogTitle>My Profile</DialogTitle>
            <DialogDescription>{name} · {roleLabel(me?.role)}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <Avatar size={96} />
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPick} data-testid="profile-photo-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="profile-photo-upload-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />{photo ? "Replace" : "Upload"} Photo</>}
              </Button>
              {photo && (
                <Button size="sm" variant="outline" className="text-red-600" onClick={removePhoto} disabled={busy} data-testid="profile-photo-remove-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
            </div>
            <p className="text-[11px] text-slate-400 text-center">PNG, JPG, or WebP · square recommended · max 5 MB</p>
          </div>
        </DialogContent>
      </Dialog>

      <FeedbackModal open={fbOpen} onOpenChange={setFbOpen} portalType={roleLabel(me?.role) === "Portfolio Manager" ? "Portfolio Manager" : undefined} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />
    </>
  );
};

export default PortalUserMenu;
